import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { STORAGE, DEFAULT_TABLE_SESSION } from '../constants/storageKeys';
import { formatMsClock } from '../utils/time';
import { diceLastTotal } from '../utils/diceSummary';
import './SessionRibbon.css';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function getTimerRemaining(timer, now) {
  if (!timer || typeof timer !== 'object') return null;
  if (timer.status === 'running' && timer.endAt) return Math.max(0, timer.endAt - now);
  if (timer.status === 'paused' && timer.pausedRemainingMs != null) return timer.pausedRemainingMs;
  if (timer.status === 'done') return 0;
  return typeof timer.durationMs === 'number' ? timer.durationMs : null;
}

export default function SessionRibbon() {
  const [, bump] = useState(0);
  const refresh = useCallback(() => bump((n) => n + 1), []);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith('mt:')) refresh();
    };
    const onPersist = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('mt-persist', onPersist);
    const id = window.setInterval(refresh, 250);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('mt-persist', onPersist);
      window.clearInterval(id);
    };
  }, [refresh]);

  const rounds = readJson(STORAGE.rounds, { round: 1 });
  const timer = readJson(STORAGE.timer, {});
  const dice = readJson(STORAGE.dice, {});
  const fp = readJson(STORAGE.firstPlayer, {});
  const kitSession = readJson(STORAGE.session, DEFAULT_TABLE_SESSION);

  const now = Date.now();
  const tRem = getTimerRemaining(timer, now);
  const timerLabel =
    timer.status === 'running' || timer.status === 'paused' || timer.status === 'done'
      ? formatMsClock(tRem ?? 0)
      : tRem != null
        ? formatMsClock(tRem)
        : '—';

  const diceTotal = diceLastTotal(dice);
  const diceLabel = diceTotal != null ? `Σ${diceTotal}` : 'idle';

  const crown =
    fp.result && typeof fp.result === 'string' && fp.result.length > 0 ? fp.result.charAt(0).toUpperCase() : '—';

  const gn = typeof kitSession.gameName === 'string' ? kitSession.gameName.trim() : '';
  const pc = Array.isArray(kitSession.players) ? kitSession.players.filter((n) => n && String(n).trim()).length : 0;
  const tableLabel =
    gn && pc ? `${gn} · ${pc}p` : gn ? gn : pc ? `${pc} players` : null;

  return (
    <nav className="session-ribbon" aria-label="Session quick status">
      {tableLabel && (
        <>
          <Link to="/" className="session-ribbon-table">
            <span className="session-ribbon-label">Table </span>
            {tableLabel}
          </Link>
          <span className="session-ribbon-dot" aria-hidden>
            ·
          </span>
        </>
      )}
      <Link to="/rounds">
        <span className="session-ribbon-label">Round </span>R{typeof rounds.round === 'number' ? rounds.round : 1}
      </Link>
      <span className="session-ribbon-dot" aria-hidden>
        ·
      </span>
      <Link to="/timer">
        <span className="session-ribbon-label">Timer </span>⏱ {timerLabel}
      </Link>
      <span className="session-ribbon-dot" aria-hidden>
        ·
      </span>
      <Link to="/dice">
        <span className="session-ribbon-label">Dice </span>🎲 {diceLabel}
      </Link>
      <span className="session-ribbon-dot" aria-hidden>
        ·
      </span>
      <Link to="/first-player">
        <span className="session-ribbon-label">1st </span>👑 {crown}
      </Link>
    </nav>
  );
}
